// src/hooks/useWallets.ts
import { env } from '@/env';
import { useGetLimuloMyWalletQuery } from '@/services/limuloApi';
import { useGetWalletsQuery } from '@/services/qbankApi';

/**
 * Il SALDO dell'utente, da UNA fonte scelta in UN punto: questo hook.
 *
 * Decisione di Luca, L-2717 (a), 2026-10-02 22:37Z (e L-2578 (a): il portafoglio migra alla facciata,
 * ADR-0180, kb#143): con `VITE_FEATURE_WALLET_FACCIATA_ENABLED` acceso la TMA legge il saldo da
 * `GET /v1/me/wallet` della facciata di Limulo; spento, da QBank diretto (`GET /wallets/get`), com'era.
 *
 * ⛔ **E' una scelta di BUILD, mai un ripiego.** «Prova la facciata, se fallisce chiedi a QBank» sarebbe un
 * guasto d'identita', non una resilienza: su staging il QBank diretto e' quello di AWS, NON quello di Limulo, e
 * mostrerebbe a un utente il saldo di un altro mondo senza che nessuno schermo lo noti. Un errore della facciata
 * (403/404 con `enableWallet` spento o senza la capability, 424, 5xx) resta un ERRORE: chi consuma lo legge
 * come «saldo non disponibile» e nasconde, **mai «0»**.
 *
 * ⛔ **La fonte non scelta NON parte**: con l'interruttore acceso `qbankApi.getWallets` e' in `skip` (niente 401
 * su `qbank-stg`, dove il login di staging non chiede la resource QBank), e con l'interruttore spento e' la
 * facciata a stare in `skip`. Entrambe le query sono chiamate ad ogni render — le regole degli hook non
 * permettono di sceglierne una a runtime — ma una sola e' mai attiva.
 *
 * Le opzioni sono quelle dei consumatori di prima (`skip`, `refetchOnMountOrArgChange`): restano LOCALI di chi le
 * passa, e valgono per la fonte scelta.
 *
 * ⚠️ PARITA' DI COMPORTAMENTO fra le due fonti: `qbankApi` ha `refetchOnFocus: false` e `keepUnusedDataFor: 120`,
 * `limuloApi` ha `true` e 60. Senza correzione la HUD, passando alla facciata, rileggerebbe il saldo a ogni focus
 * — proprio cio' che `WalletLevels` e `verify-missions` vietano su `qbankApi`. ⇒ `refetchOnFocus: false` qui, sulla
 * sottoscrizione, e `keepUnusedDataFor: 120` sull'endpoint. Resta UNA differenza, dichiarata: i ritentativi
 * (`limuloApi` ritenta solo le letture su 5xx/rete; `qbankApi` ritentava una volta ogni errore).
 * ⛔ `skip` si scrive DOPO lo spread: prima, `...options` (con `skip: false` dei consumatori) riaccenderebbe la
 * fonte non scelta.
 */
export function useWallets(options: { skip?: boolean; refetchOnMountOrArgChange?: boolean } = {}) {
  const daFacciata = env.featureWalletFacciataEnabled;
  const diretto = useGetWalletsQuery(undefined, { ...options, skip: options.skip || daFacciata });
  const facciata = useGetLimuloMyWalletQuery(undefined, {
    ...options,
    refetchOnFocus: false,
    skip: options.skip || !daFacciata,
  });
  return daFacciata ? facciata : diretto;
}
